/* istanbul ignore file */
import { StylesConfig, ControlProps, CSSObjectWithLabel, OptionProps } from 'react-select';

import { CSSObject } from '@emotion/react';

import { ISelectMultiOption } from './components/SelectMulti';

// Reusable styles for both multi and non-multi select
const commonStyles = {
  menu: (provided: CSSObject) =>
    ({
      ...provided,
      zIndex: 9999,
    }) as CSSObject,
  menuPortal: (provided: CSSObject) =>
    ({
      ...provided,
      zIndex: 9999,
    }) as CSSObject,
  valueContainer: (provided: CSSObject) =>
    ({
      ...provided,
      height: '44px',
      padding: '0 6px',
      zIndex: 5,
    }) as CSSObject,
  input: (provided: CSSObject) =>
    ({
      ...provided,
      margin: '0px',
      border: '0px',
    }) as CSSObject,
  indicatorSeparator: () => ({
    display: 'none',
  }),
  indicatorsContainer: (provided: CSSObject) =>
    ({
      ...provided,
      height: '44px',
    }) as CSSObject,
};

export const reactSelectMultiStyles: StylesConfig<ISelectMultiOption, true> = {
  control: (provided: CSSObjectWithLabel, state: ControlProps<ISelectMultiOption, true>) =>
    ({
      ...provided,
      borderColor: state.menuIsOpen ? 'var(--accent)' : 'var(--border-color-muted)',
      borderRadius: 'var(--radius)',
      minHeight: '44px',
      borderWidth: '0px',
      backgroundColor: 'var(--input-bg)',
      boxShadow: state.isFocused || state.menuIsOpen ? '0 0 0 1px var(--accent)' : 'none',
    }) as CSSObjectWithLabel,
  // Table-head filters render inside a centred, semibold <th>: keep the checkbox + label rows,
  // the placeholder and the menu left-aligned and at normal weight
  option: (provided: CSSObjectWithLabel, state: OptionProps<ISelectMultiOption, true>) =>
    ({
      ...provided,
      display: 'flex',
      alignItems: 'center',
      color: '#000',
      justifyContent: 'flex-start',
      textAlign: 'left',
      fontWeight: 'normal',
      gap: '10px',
      backgroundColor: state.isFocused
        ? 'color-mix(in srgb, var(--accent) 20%, transparent)'
        : 'transparent',
    }) as CSSObject,
  placeholder: (provided: CSSObject) =>
    ({
      ...provided,
      textAlign: 'left',
      fontWeight: 'normal',
    }) as CSSObject,
  ...commonStyles,
  menu: (provided: CSSObject) =>
    ({
      ...commonStyles.menu(provided),
      textAlign: 'left',
      fontWeight: 'normal',
    }) as CSSObject,
};

export const reactFormSelectMultiStyles = {
  ...reactSelectMultiStyles,
  valueContainer: (provided: CSSObject) =>
    ({
      ...provided,
      padding: '4px 8px',
      zIndex: 5,
    }) as CSSObject,
  control: (provided: CSSObjectWithLabel, state: ControlProps<ISelectMultiOption, true>) =>
    ({
      ...provided,
      borderRadius: 'var(--radius)',
      minHeight: '44px',
      border: '1px solid var(--border-color-muted)',
      borderColor:
        state.isFocused || state.menuIsOpen ? 'var(--accent)' : 'var(--border-color-muted)',
      boxShadow: state.isFocused || state.menuIsOpen ? '0 0 0 1px var(--accent)' : 'none',
      '&:hover': {
        borderColor: 'var(--accent)',
      },
    }) as CSSObjectWithLabel,
  option: (provided: CSSObjectWithLabel, state: OptionProps<ISelectMultiOption, true>) =>
    ({
      ...provided,
      backgroundColor: state.isFocused
        ? 'color-mix(in srgb, var(--accent) 20%, transparent)'
        : 'transparent',
      '&:hover': { backgroundColor: 'color-mix(in srgb, var(--accent) 30%, transparent)' },
    }) as CSSObject,
};

// Styles for non-multi select accepts Generic type
export const reactSelectStyles = <OptionType, IsMulti extends boolean = false>(): StylesConfig<
  OptionType,
  IsMulti
> => ({
  control: (provided: CSSObjectWithLabel, state: ControlProps<OptionType, IsMulti>) =>
    ({
      ...provided,
      borderColor:
        state.isFocused || state.menuIsOpen ? 'var(--accent)' : 'var(--border-color-muted)',
      borderRadius: 'var(--radius)',
      minHeight: '44px',
      borderWidth: '1px',
    }) as CSSObjectWithLabel,
  ...commonStyles,
});
